import { useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Plus, UserPlus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../../api/errors';
import { qk } from '../../api/keys';
import { useUserMap } from '../../api/org';
import { useCreateTask } from '../../api/tasks';
import type { Task } from '../../api/types';
import { useSession } from '../../app/session';
import { Avatar } from '../../components/ui/Avatar';
import { SearchSelect } from '../../components/ui/Combobox';
import { DatePanel } from '../../components/ui/DatePicker';
import { useEscapeLayer } from '../../components/ui/escape';
import { Popover } from '../../components/ui/Popover';
import { cn } from '../../lib/cn';
import { formatDue, kyivHour, nextWorkingDayISO, todayISO } from '../../lib/dates';
import { useUserOptions } from './TaskControls';

/*
 * Швидке додавання задач «як в Asana»: рядок із назвою, виконавцем і строком; Enter — створити й одразу писати наступну,
 * Esc — закрити. Правила фірми (виконавець, строк, проєкт) застосовує сервер — його пояснення показуються під рядком.
 */

/** Кнопка-аватар для вибору виконавця (без обгортки поля). */
export function UserChipPicker({
  value,
  onChange,
  disabled,
  label = 'Виконавець',
  pickLabel = 'Обрати виконавця',
}: {
  value: string | null;
  onChange: (id: string) => void;
  disabled?: boolean;
  /** Роль людини в цьому полі (за замовчуванням — виконавець). */
  label?: string;
  pickLabel?: string;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const users = useUserMap();
  const options = useUserOptions();
  const u = value ? users.get(value) : undefined;
  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={u ? `${label}: ${u.name}, змінити` : pickLabel}
        title={u ? `${label}: ${u.name}` : label}
        className="inline-flex size-7 shrink-0 items-center justify-center rounded-full text-fg-3 hover:bg-surface-2 hover:text-fg"
      >
        {u ? <Avatar name={u.name} src={u.avatarUrl} size={22} /> : <UserPlus className="size-4" aria-hidden />}
      </button>
      <SearchSelect
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchor={anchor}
        placement="bottom-end"
        options={options}
        value={value}
        label={label}
        placeholder="Ім’я або посада"
        onSelect={onChange}
      />
    </>
  );
}

/** Кнопка-строк із календарем і швидкими варіантами («сьогодні», «завтра», «наступний робочий день»). */
export function DateChipPicker({ value, onChange, allowClear, disabled }: { value: string | null; onChange: (v: string | null) => void; allowClear?: boolean; disabled?: boolean }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={value ? `Строк: ${formatDue(value)}, змінити` : 'Обрати строк'}
        className={cn(
          'inline-flex h-7 shrink-0 items-center gap-1 rounded-sm px-1.5 text-meta whitespace-nowrap hover:bg-surface-2',
          value ? 'text-fg-2' : 'text-fg-3',
        )}
      >
        <CalendarDays className="size-3.5" aria-hidden />
        <span className="tnum">{value ? formatDue(value) : 'Строк'}</span>
      </button>
      <Popover open={!!anchor} onClose={() => setAnchor(null)} anchor={anchor} placement="bottom-end" aria-label="Строк">
        <DatePanel
          value={value}
          allowClear={allowClear}
          onSelect={(v) => {
            setAnchor(null);
            onChange(v);
          }}
        />
      </Popover>
    </>
  );
}

/** Типовий строк нової задачі: після 15:00 доручення іншому — наступний робочий день (правило фірми). */
export function defaultQuickDue(assigneeId: string | null, meId: string, lateHour = 15): string {
  if (assigneeId && assigneeId !== meId && kyivHour() >= lateHour) return nextWorkingDayISO();
  return todayISO();
}

export type QuickAddDefaults = {
  projectId?: string;
  sectionId?: string;
  parentId?: string;
  assigneeId?: string | null;
  dueOn?: string | null;
  followerIds?: string[];
  /** Строк не обов'язковий (етап дозволяє задачі без строку). */
  allowNoDue?: boolean;
};

type QuickAddProps = {
  defaults: QuickAddDefaults;
  onClose: () => void;
  onCreated?: (task: Task) => void;
  placeholder?: string;
  /** Відкрити повну форму з уже введеними значеннями. */
  onFull?: (draft: { title: string; assigneeId: string | null; dueOn: string | null }) => void;
  className?: string;
  /** Варіант для колонки дошки (вертикальна картка). */
  variant?: 'row' | 'card';
};

export function QuickAddTask({ defaults, onClose, onCreated, placeholder = 'Назва задачі', onFull, className, variant = 'row' }: QuickAddProps) {
  const create = useCreateTask();
  const qc = useQueryClient();
  const { userId, policy } = useSession();
  const [title, setTitle] = useState('');
  const [assigneeId, setAssigneeId] = useState<string | null>(defaults.assigneeId === undefined ? userId : defaults.assigneeId);
  const [dueOn, setDueOn] = useState<string | null>(
    defaults.dueOn !== undefined ? defaults.dueOn : defaults.allowNoDue ? null : defaultQuickDue(defaults.assigneeId ?? userId, userId, policy?.lateAssignHour ?? 15),
  );
  const [error, setError] = useState<ApiError | null>(null);
  const [created, setCreated] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useEscapeLayer(true, onClose);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = () => {
    const t = title.trim();
    if (!t) {
      onClose();
      return;
    }
    create.mutate(
      {
        title: t,
        projectId: defaults.projectId,
        sectionId: defaults.sectionId,
        parentId: defaults.parentId,
        assigneeId,
        dueOn: dueOn ?? undefined,
        followerIds: defaults.followerIds,
      },
      {
        onSuccess: (task) => {
          setTitle('');
          setError(null);
          setCreated((n) => n + 1);
          if (defaults.parentId) {
            void qc.invalidateQueries({ queryKey: qk.taskPart(defaults.parentId, 'subtasks') });
            void qc.invalidateQueries({ queryKey: qk.task(defaults.parentId) });
          }
          void qc.invalidateQueries({ queryKey: qk.tasksAll });
          if (defaults.projectId) void qc.invalidateQueries({ queryKey: qk.project(defaults.projectId) });
          onCreated?.(task);
          inputRef.current?.focus();
        },
        onError: (e) => setError(e instanceof ApiError ? e : null),
      },
    );
  };

  const messages = error ? [error.detail, ...error.messagesExcept([])].filter((m, i, a) => m && a.indexOf(m) === i) : [];

  return (
    <div
      ref={boxRef}
      className={cn('flex flex-col gap-1', className)}
      onBlur={(e) => {
        // Клік поза рядком, коли назву не введено, — закрити (як в Asana).
        if (!title.trim() && !boxRef.current?.contains(e.relatedTarget as Node | null) && !(e.relatedTarget as Element | null)?.closest?.('[data-ers-layer]')) {
          window.setTimeout(() => {
            if (!boxRef.current?.contains(document.activeElement) && !document.activeElement?.closest('[data-ers-layer]')) onClose();
          }, 0);
        }
      }}
    >
      <div
        className={cn(
          'flex items-center gap-1.5 rounded-sm border bg-surface px-1.5 focus-within:border-focus',
          error ? 'border-danger' : 'border-line-input',
          variant === 'card' ? 'flex-wrap py-1.5' : 'min-h-9 py-0.5',
        )}
      >
        {variant === 'row' && <span className="ml-0.5 size-[18px] shrink-0 rounded-full border border-dashed border-line-input" aria-hidden />}
        <input
          ref={inputRef}
          aria-label={placeholder}
          aria-invalid={error ? true : undefined}
          placeholder={placeholder}
          value={title}
          maxLength={500}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
              e.preventDefault();
              submit();
            }
          }}
          className={cn('min-w-0 flex-1 bg-transparent px-1 text-body text-fg outline-none placeholder:text-fg-3', variant === 'card' && 'basis-full')}
        />
        <span className={cn('flex shrink-0 items-center', variant === 'card' && 'ml-auto')}>
          <UserChipPicker value={assigneeId} onChange={setAssigneeId} />
          <DateChipPicker value={dueOn} onChange={setDueOn} allowClear={defaults.allowNoDue} />
        </span>
      </div>
      {messages.length > 0 && (
        <p role="alert" className="px-1 text-meta text-danger-fg">
          {messages.join(' ')}
        </p>
      )}
      <p className="flex flex-wrap items-center gap-x-2 px-1 text-caption text-fg-3">
        {create.isPending ? 'Створюється…' : created > 0 ? `Створено: ${created}. Пишіть наступну.` : 'Enter — створити й додати наступну · Esc — закрити'}
        {onFull && (
          <button type="button" onClick={() => onFull({ title, assigneeId, dueOn })} className="underline underline-offset-2 hover:text-fg">
            Повна форма
          </button>
        )}
      </p>
    </div>
  );
}

/** Рядок-кнопка «+ Додати задачу» (у розділі переліку, колонці дошки, «Моїй роботі»). */
export function AddTaskRowButton({ onClick, label = 'Додати задачу', className, tour }: { onClick: () => void; label?: string; className?: string; tour?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-tour={tour}
      className={cn('flex min-h-9 w-full items-center gap-2 rounded-sm px-2 text-left text-meta text-fg-3 hover:bg-surface-2 hover:text-fg', className)}
    >
      <Plus className="size-4" aria-hidden />
      {label}
    </button>
  );
}
